import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';

import { formConsumer, Field, InputGroup, Select } from 'core/form';
import Dialog from 'core/components/Dialog';
import { Text, Card, Heading, Box, CalloutOutline, Flex, Tag, Button, Icon, Tooltip } from 'core/components';
import PolicyLink from 'app/components/links/PolicyLink';

import ViewInExplorerButton from 'app/components/dataviews/tools/ViewInExplorerButton';
import FlowSpecIPField from './FlowSpecIPField';
import FlowSpecMatchField from './FlowSpecMatchField';
import FlowSpecMatchInfoField from './FlowSpecMatchInfoField';

@inject('$dictionary', '$mitigations')
@formConsumer
@observer
export default class MethodFlowspec extends Component {
  static propTypes = {
    // in case the method has some inferred values, they can be deduced in the context of a wrapping alarm object (model)
    alertModel: PropTypes.object,
    // in case the flowspec details are shown in a mitigation context, we derive details from there
    mitigationContext: PropTypes.object,
    // this prop turns the entire component into just an informational panel
    // entirely non-interactional, simply meant to use the complex fields defined in the form structure
    infoMode: PropTypes.bool,
    // self explanatory - detail modal will use the method's name as title
    useNameAsTitle: PropTypes.bool,
    // self explanatory - show a view in DE button, based on alarm context
    showViewInDataExplorerButton: PropTypes.bool,
    // this prop makes the info mode only provide minimal info and compact spacing
    minimal: PropTypes.bool,
    // wrap the details of the method in a flex container
    wrap: PropTypes.bool,
    // potentially override title
    title: PropTypes.node
  };

  static defaultProps = {
    alertModel: null,
    mitigationContext: null,
    infoMode: false,
    useNameAsTitle: false,
    showViewInDataExplorerButton: false,
    minimal: false,
    wrap: false,
    title: null
  };

  state = {
    invalidDependentPolicies: [],
    mitigationDetailsDialogOpen: false
  };

  get dscpOptions() {
    const { $dictionary } = this.props;
    const { dscp } = $dictionary.filterFieldValueOptions;
    return dscp;
  }

  validateDependentPolicy = () => {
    const { form, $mitigations } = this.props;

    if (!form.model.isNew) {
      this.setState({
        invalidDependentPolicies: $mitigations
          .getPoliciesUsingMethod(form.model.id)
          .filter((policy) => !policy.isFlowspecMethodValid(form.getValues()))
      });
    }
  };

  getTrafficActionValueField = (trafficAction) => {
    if (trafficAction === 'rateLimit') {
      return (
        <Flex>
          <Field
            name="flowspec.trafficActions.rateLimit.value"
            placeholder="Rate Limit bytes/sec"
            showLabel={false}
            onChange={this.onRateLimitChange}
            width={240}
            showRequired
          >
            <InputGroup
              type="number"
              small
              rightElement={
                <Tag minimal m="4px">
                  bytes/sec
                  <Tooltip content="Some devices, such as older Juniper routers, may interpret this value as bits/sec">
                    <Icon icon="help" color="muted" ml="4px" cursor="pointer" />
                  </Tooltip>
                </Tag>
              }
            />
          </Field>
        </Flex>
      );
    }

    if (trafficAction === 'discard') {
      return (
        <Field name="flowspec.trafficActions.discard.empty" hidden value={null}>
          <InputGroup hidden />
        </Field>
      );
    }

    if (trafficAction === 'markDSCP') {
      return (
        <Field
          name="flowspec.trafficActions.markDSCP.mark"
          placeholder="Select a value..."
          showLabel={false}
          inputStyle={{ minWidth: 200 }}
          options={this.dscpOptions}
          showRequired
        >
          <Select showFilter />
        </Field>
      );
    }

    if (trafficAction === 'routeTargetRedirect') {
      return (
        <Field
          name="flowspec.trafficActions.routeTargetRedirect.routeTarget"
          placeholder="xxxxx:yyyyy route-target"
          showLabel={false}
          width={200}
          showRequired
        >
          <InputGroup />
        </Field>
      );
    }

    if (trafficAction === 'nextHopRedirect') {
      return (
        <Flex alignItems="center">
          <Field
            mb={0}
            showRequired
            flex={1}
            showLabel={false}
            name="flowspec.trafficActions.nextHopRedirect.nextHopV4"
          >
            <InputGroup fill />
          </Field>
          <Box mx={1} alignItems="center">
            <Text small muted fontWeight="bold">
              OR
            </Text>
          </Box>
          <Field
            mb={0}
            showRequired
            flex={1}
            showLabel={false}
            name="flowspec.trafficActions.nextHopRedirect.nextHopV6"
          >
            <InputGroup fill />
          </Field>
        </Flex>
      );
    }

    return null;
  };

  toggleMitigationDetailsDialog = () => {
    this.setState((prevState) => ({
      mitigationDetailsDialogOpen: !prevState.mitigationDetailsDialogOpen
    }));
  };

  render() {
    const {
      $mitigations,
      form,
      showViewInDataExplorerButton,
      alertModel,
      mitigationContext,
      infoMode,
      title,
      useNameAsTitle,
      minimal,
      wrap
    } = this.props;
    const { mitigationDetailsDialogOpen, invalidDependentPolicies } = this.state;
    const trafficAction = form.getValue('flowspec.trafficAction');
    const methodName = form.getValue('name');
    const FlowSpecMatchFieldToUse = infoMode ? FlowSpecMatchInfoField : FlowSpecMatchField;
    const detailProps = {
      alertModel,
      mitigationContext,
      minimal,
      infoMode
    };

    const flowspecDetails = (
      <>
        <FlowSpecIPField {...detailProps} matchFieldType="destIpCidr" onInferChange={this.validateDependentPolicy} />
        <FlowSpecIPField {...detailProps} matchFieldType="sourceIpCidr" onInferChange={this.validateDependentPolicy} />
        <FlowSpecMatchFieldToUse
          {...detailProps}
          matchFieldType="protocols"
          onInferChange={this.validateDependentPolicy}
          showInferField
        />
        <FlowSpecMatchFieldToUse
          {...detailProps}
          matchFieldType="srcPorts"
          onInferChange={this.validateDependentPolicy}
          showInferField
        />
        <FlowSpecMatchFieldToUse
          {...detailProps}
          matchFieldType="dstPorts"
          onInferChange={this.validateDependentPolicy}
          showInferField
        />
        <FlowSpecMatchFieldToUse
          {...detailProps}
          matchFieldType="icmpTypes"
          onInferChange={this.validateDependentPolicy}
        />
        <FlowSpecMatchFieldToUse
          {...detailProps}
          matchFieldType="icmpCodes"
          onInferChange={this.validateDependentPolicy}
        />
        <FlowSpecMatchFieldToUse
          {...detailProps}
          matchFieldType="tcpFlags"
          onInferChange={this.validateDependentPolicy}
          operators={[
            {
              label: 'includes',
              value: 'equals'
            },
            {
              label: 'does not include',
              value: 'notEquals'
            }
          ]}
        />
        <FlowSpecMatchFieldToUse
          {...detailProps}
          matchFieldType="packetLengths"
          onInferChange={this.validateDependentPolicy}
        />
        <FlowSpecMatchFieldToUse {...detailProps} matchFieldType="dscps" onInferChange={this.validateDependentPolicy} />
        <FlowSpecMatchFieldToUse
          {...detailProps}
          matchFieldType="fragments"
          onInferChange={this.validateDependentPolicy}
          operators={[
            {
              label: 'equals',
              value: 'equals'
            },
            {
              label: 'does not equal',
              value: 'notEquals'
            }
          ]}
        />
      </>
    );

    return (
      <Box width="100%">
        <Card p="12px" pb={0}>
          {title || (
            <Flex alignItems="center" justifyContent="space-between" py={infoMode ? 1 : 0}>
              <Heading level={5} mb={1} fontWeight="heavy">
                {useNameAsTitle ? methodName : 'Traffic Matching'}
              </Heading>
              {infoMode && minimal ? (
                <Button ml={1} mb={1} text="View Method Details" onClick={this.toggleMitigationDetailsDialog} />
              ) : null}
            </Flex>
          )}
          {wrap ? (
            <Flex flexWrap="wrap" justifyContent="space-between">
              {flowspecDetails}
            </Flex>
          ) : (
            flowspecDetails
          )}
          {invalidDependentPolicies.length > 0 && (
            <CalloutOutline intent="warning" mb={3}>
              Enabling Infer from Alarm may causes issues with the following policies:
              <Box mt={1} ml={1}>
                {invalidDependentPolicies.map((policy) => (
                  <PolicyLink key={policy.id} id={policy.id}>
                    {policy.get('name')}
                  </PolicyLink>
                ))}
              </Box>
            </CalloutOutline>
          )}
          {showViewInDataExplorerButton && alertModel && (
            <ViewInExplorerButton
              mt={1}
              mb={2}
              textAlign="left"
              minimal
              openInNewWindow
              query={$mitigations.getFlowspecPreviewQuery({
                alertModel,
                flowspecDetails: form.getValue('flowspec'),
                context: mitigationContext
              })}
            />
          )}
        </Card>
        {!infoMode && (
          <Card mt="12px" p="12px">
            <Heading level={5} mb={1} fontWeight="heavy">
              Traffic Filtering Actions
            </Heading>

            <Flex borderTop="thin">
              <Field flex={2} name="flowspec.trafficAction" showRequired mt={2} mr={1}>
                <Select fill />
              </Field>
              <Box flex={3} pt="34px" key={trafficAction}>
                {this.getTrafficActionValueField(trafficAction)}
              </Box>
            </Flex>
          </Card>
        )}
        <Dialog
          isOpen={mitigationDetailsDialogOpen}
          title="Mitigation Method Details"
          canEscapeKeyClose
          canClickOutside
          isCloseButtonShown
          onClose={this.toggleMitigationDetailsDialog}
          width="400px"
        >
          <Dialog.Body>
            <MethodFlowspec
              infoMode
              title={
                <Heading level={5} mb={1} fontWeight="heavy">
                  {form.getValue('name')}
                </Heading>
              }
              form={form}
            />
          </Dialog.Body>
        </Dialog>
      </Box>
    );
  }
}
